// src/components/account/FamilyJewelsDashboardPage.tsx

import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Divider, Spin, Alert } from 'antd';
import { DollarCircleOutlined, DownCircleOutlined, UpCircleOutlined, ProfileOutlined, MoneyCollectOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { useGlobalContext } from '../../store/GlobalContext';

import { Asset, CashFlow, Liability, User } from '../../types';
import { getRecentAssets } from '../assets/asset.service';
import { getRecentCashflows } from '../cashflows/cashflow.service';
import { getRecentParticipants } from '../participant/participantService';
import { SummaryData, getSummary } from './summaryService';

const FamilyJewelsDashboardPage: React.FC = () => {
  const { selectedEstate } = useGlobalContext();
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [cashflows, setCashflows] = useState<CashFlow[]>([]);
  const [liabilities, setLiabilities] = useState<Liability[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [participants, setParticipants] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const estateId = selectedEstate?.uuid || '';

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!estateId) {
        if (isMounted) {
          setError('No estate selected.');
          setLoading(false);
        }
        return;
      }

      try {
        // Fetch all data concurrently
        const [summaryData, recentCashflows, recentAssets, ] = await Promise.all([
          getSummary(estateId),
          getRecentCashflows(estateId, 5),
          // getRecentLiabilities(estateId, 5),
          getRecentAssets(estateId, 5),
          // getRecentParticipants(estateId, 5),
        ]);

        if (isMounted) {
          setSummary(summaryData);
          setCashflows(recentCashflows);
          // setLiabilities(recentLiabilities);
          setAssets(recentAssets);
          // setParticipants(recentParticipants);
        }
      } catch (err: any) {
        console.error(err);
        if (isMounted) {
          setError(err.message || 'Failed to fetch dashboard data.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [estateId]);

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin tip="Loading dashboard..." size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[16, 16]}>
        {/* Summary Cards */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card className="dashboard-card">
            <Statistic
              title={<span><DollarCircleOutlined style={{ marginRight: '8px' }} />Gross Estate Value</span>}
              value={summary?.estateValue}
              prefix={<DollarCircleOutlined />}
              precision={2}
              formatter={(value) => `${value.toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card className="dashboard-card">
            <Statistic
              title={<span><MoneyCollectOutlined style={{ marginRight: '8px' }} />Total Assets</span>}
              value={summary?.totalAssets}
            />
          </Card>
        </Col>
        {/* <Col xs={24} sm={12} md={8} lg={6}>
          <Card className="dashboard-card">
            <Statistic
              title={<span><DownCircleOutlined style={{ marginRight: '8px' }} />Total Liabilities</span>}
              value={summary?.totalLiabilities}
            />
          </Card>
        </Col> */}
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card className="dashboard-card">
            <Statistic
              title={<span><UpCircleOutlined style={{ marginRight: '8px' }} />Total Inbound Cashflow</span>}
              value={summary?.totalInboundCashflow}
              prefix="$"
              precision={2}
              valueStyle={{ color: '#3f8600' }}
              formatter={(value) => `${value.toLocaleString()}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card className="dashboard-card">
            <Statistic
              title={<span><DownCircleOutlined style={{ marginRight: '8px' }} />Total Outbound Cashflow</span>}
              value={summary?.totalOutboundCashflow}
              prefix="$"
              precision={2}
              valueStyle={{ color: '#cf1322' }}
              formatter={(value) => `${value.toLocaleString()}`}
            />
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Recent Transactions Section */}
      <Row gutter={[16, 16]}>
        {/* Recent Cashflow Transactions */}
        <Col xs={24} lg={12}>
          <Card title="Recent Cashflow Transactions" className="dashboard-section-card">
            <Table
              dataSource={cashflows}
              pagination={false}
              columns={[
                { title: "Name", dataIndex: "name", key: "name" },
                { title: "Type", dataIndex: "type", key: "type", render: (type: string) => (
                  <Tag color={type === "Inbound" ? "green" : "red"}>{type}</Tag>
                )},
                { title: "Amount", dataIndex: "amount", key: "amount", render: (amount: string) => (
                  <span>${parseFloat(amount).toLocaleString()}</span>
                ) },
                { title: "Date", dataIndex: "date", key: "date" },
              ]}
              rowKey={(record) => record.name + record.createdAt}
              bordered
              size="small"
            />
          </Card>
        </Col>

        {/* Recent Liabilities */}
        <Col xs={24} lg={12}>
          <Card title="Recent Liabilities" className="dashboard-section-card">
            <Table
              dataSource={liabilities}
              pagination={false}
              columns={[
                { title: "Name", dataIndex: "name", key: "name" },
                { title: "Amount", dataIndex: "amount", key: "amount", render: (amount: string) => (
                  <span>${parseFloat(amount).toLocaleString()}</span>
                ) },
                { title: "Due Date", dataIndex: "dueDate", key: "dueDate" },
              ]}
              rowKey={(record) => record.name + record.createdAt}
              bordered
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        {/* Recently Added Assets */}
        <Col xs={24} lg={12}>
          <Card title="Recently Added Assets" className="dashboard-section-card">
            <Table
              dataSource={assets}
              pagination={false}
              columns={[
                { title: "Name", dataIndex: "name", key: "name" },
                { title: "Value", dataIndex: "value", key: "value", render: (value: string) => (
                  <span>${parseFloat(value).toLocaleString()}</span>
                ) },
                { title: "Date Added", dataIndex: "dateAdded", key: "dateAdded" },
              ]}
              rowKey={(record) => record.name + record.createdAt}
              bordered
              size="small"
            />
          </Card>
        </Col>

        {/* Recent Participants */}
        <Col xs={24} lg={12}>
          <Card title="Recent Participants" className="dashboard-section-card">
            <Table
              dataSource={participants}
              pagination={false}
              columns={[
                { title: "Name", dataIndex: "name", key: "name" },
                { title: "Role", dataIndex: "role", key: "role" },
                { title: "Date Added", dataIndex: "dateAdded", key: "dateAdded" },
              ]}
              rowKey={(record) => record.firstName + record.createdOn}
              bordered
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FamilyJewelsDashboardPage;
